/**
 * AITools displayed in the AI Tools tab of the Teacher Dashboard.
 */

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import React, {useCallback, useEffect, useState} from "react";

import {IntegerField} from "@khanacademy/event-schemas";
import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import SearchField from "@khanacademy/wonder-blocks-search-field";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    publishAnalyticsEventForMinorConversion,
    useMarkConversion,
} from "@khan/analytics-learning-time";
import {useQuery} from "@khan/data-gql";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import AIToolCardsEmpty from "./ai-tool-cards-empty.tsx";
import AIToolCards, {KhanmigoToolsDashboard} from "./ai-tool-cards.tsx";
import AIToolCategories from "./ai-tool-categories.tsx";
import {ToolCategories, type Tool} from "./ai-tools-list.ts";
import {TEACHER_TOOLS_FETCH} from "./ai-tools-queries.ts";
import type {getFavoritedTeacherTools_aiGuideFavoritedTeacherTools as TeacherTool} from "./hooks/__graphql-types__/getFavoritedTeacherTools.ts";
import {useAddFavoritedTeacherTool} from "./hooks/use-add-favorited-teacher-tool-mutation.ts";
import useCategoriesToRender from "./hooks/use-categories-to-render.ts";
import {useDeleteFavoritedTeacherTool} from "./hooks/use-delete-favorited-teacher-tool-mutation.ts";
import useFilteredToolsByCategoryAndSearch from "./hooks/use-filtered-tools-by-category-and-search.ts";
import useFilteredTools from "./hooks/use-filtered-tools.ts";
import {useGetFavoritedTeacherTools} from "./hooks/use-get-favorited-teacher-tools-query.ts";
import LanguageAvailabilityBanner from "./language-availability-banner.tsx";
import {getToolId, type Platform} from "./utils.ts";

type Props = {
    platform: Platform;
};

const AITools = (props: Props): React.ReactElement => {
    const [fullToolsState, setFullToolsState] = useState<Array<Tool>>([]);
    const [hasRetrievedFavoritedData, setHasRetrievedFavoritedData] =
        useState<boolean>(false);
    const [favoritedTeacherTools, setFavoritedTeacherTools] = useState<
        Array<TeacherTool>
    >([]);

    const [category, setCategory] = useState<ToolCategories>(
        ToolCategories.All,
    );
    const [searchTerm, setSearchTerm] = useState<string>("");

    // Fetching teacher tools and favorited tools results.
    const [teacherToolsResult] = useQuery(TEACHER_TOOLS_FETCH);
    const {
        favoritedTeacherTools: favoritedTeacherToolsResult,
        status: favoritedTeacherToolsStatus,
    } = useGetFavoritedTeacherTools();

    useEffect(() => {
        if (
            favoritedTeacherToolsStatus === "success" &&
            !hasRetrievedFavoritedData
        ) {
            setHasRetrievedFavoritedData(true);
            setFavoritedTeacherTools(favoritedTeacherToolsResult);
        }
    }, [
        favoritedTeacherTools,
        favoritedTeacherToolsResult,
        favoritedTeacherToolsStatus,
        hasRetrievedFavoritedData,
    ]);

    // Filtering based on access
    const filteredByAccess = useFilteredTools(
        teacherToolsResult,
        favoritedTeacherTools,
        favoritedTeacherToolsStatus,
    );

    // Mutation to add or delete favorited tools.
    const addFavoritedTeacherTool = useAddFavoritedTeacherTool();
    const deleteFavoritedTeacherTool = useDeleteFavoritedTeacherTool();

    const updateFullToolList = useCallback(
        (configName, userFavorited) => {
            const createFavoritedTool =
                teacherToolsResult.status === "success"
                    ? getToolId(
                          teacherToolsResult?.data?.aiGuideTeacherTools,
                          configName,
                      )
                    : undefined;

            if (createFavoritedTool) {
                if (userFavorited) {
                    addFavoritedTeacherTool({
                        toolID: createFavoritedTool.id,
                        configName: createFavoritedTool.configName,
                    });
                } else {
                    deleteFavoritedTeacherTool({
                        toolID: createFavoritedTool.id,
                        configName: createFavoritedTool.configName,
                    });
                }
            }

            setFullToolsState((prevFullToolsState) =>
                prevFullToolsState.map((tool) =>
                    tool.configName === configName
                        ? {
                              ...tool,
                              categories: userFavorited
                                  ? [
                                        ...tool.categories,
                                        ToolCategories.Favorites,
                                    ]
                                  : tool.categories.filter(
                                        (category) =>
                                            category !==
                                            ToolCategories.Favorites,
                                    ),
                          }
                        : tool,
                ),
            );
        },
        [
            addFavoritedTeacherTool,
            deleteFavoritedTeacherTool,
            teacherToolsResult,
        ],
    );

    useEffect(() => {
        setFullToolsState(filteredByAccess);
    }, [filteredByAccess]);

    // Filter tools based on the selected category and search term.
    const filteredTools: Tool[] = useFilteredToolsByCategoryAndSearch(
        fullToolsState,
        category,
        searchTerm,
    );

    const handleBlur = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiTeacherToolsToolSearched",
            eventSchemaVersion: 1,
            searchTerm: searchTerm,
            numMatches: new IntegerField(filteredTools.length),
        });
    };

    const markConversion = useMarkConversion();

    useEffect(() => {
        markConversion({
            id: "pageview_khanmigo_teacher_tools_dashboard",
        });
    }, [markConversion]);

    // Determine the categories to render based on the teacher's Khanmigo access.
    const categoriesToRender: ToolCategories[] =
        useCategoriesToRender(fullToolsState);

    if (
        teacherToolsResult.status === "loading" ||
        favoritedTeacherToolsStatus === "loading"
    ) {
        return <CircularSpinner />;
    }

    return (
        <View testId="teacher-tools-container">
            <LanguageAvailabilityBanner
                kaLocale={RequestInfo.kaLocale}
                isParentDashboard={false}
            />
            <View style={styles.filter}>
                <AIToolCategories
                    selected={category}
                    setSelected={setCategory}
                    categoriesToRender={categoriesToRender}
                />
                <SearchField
                    value={searchTerm}
                    onChange={setSearchTerm}
                    style={styles.search}
                    placeholder={t`Search`}
                    onBlur={handleBlur}
                />
            </View>
            {filteredTools.length > 0 ? (
                <AIToolCards
                    filteredTools={filteredTools}
                    category={category}
                    updateFullToolList={updateFullToolList}
                    platform={props.platform}
                    khanmigoToolsDashboard={KhanmigoToolsDashboard.Teacher}
                />
            ) : (
                <AIToolCardsEmpty
                    errorType={
                        category === ToolCategories.Favorites
                            ? "noFavorites"
                            : "noResult"
                    }
                />
            )}
        </View>
    );
};

export default AITools;

const styles = StyleSheet.create({
    filter: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: spacing.xLarge_32,

        [mediaQueries.mdOrSmaller]: {
            alignItems: "stretch",
            flexDirection: "column",
            marginBottom: spacing.medium_16,
        },
    },
    search: {
        [mediaQueries.mdOrSmaller]: {
            marginTop: spacing.medium_16,
        },
    },
});
